import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import type { PlanCatalogItem, PlanFeatureKey, ProductTier } from '@/domain/types/planCatalog';

export interface UpdatePlanCapabilitiesPayload {
  productTier?: ProductTier;
  features?: Array<{ key: PlanFeatureKey; enabled: boolean }>;
  communication?: Partial<PlanCatalogItem['communication']>;
}

export const usePlanCatalog = () => {
  const [plans, setPlans] = useState<PlanCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await httpClient.get<PlanCatalogItem[]>('/plans');
      const items = (response.data ?? []).map((p) => ({
        ...p,
        productTier: p.productTier ?? 'BASE',
        features: p.features ?? [],
        communication: p.communication ?? null,
        enabledFeatureCount: p.enabledFeatureCount ?? (p.features ?? []).filter((f) => f.enabled).length,
      }));
      setPlans(items);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('No se pudieron cargar los planes'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const updateCapabilities = useCallback(
    async (planId: number, payload: UpdatePlanCapabilitiesPayload) => {
      const response = await httpClient.put<PlanCatalogItem>(`/plans/${planId}/capabilities`, payload);
      return response.data;
    },
    [],
  );

  return { plans, isLoading, error, refetch: loadPlans, updateCapabilities };
};
